<template>
  <div class="project-page">
    <div class="header-area">
      <div class="header">
        <span class="title">我的项目</span>
      </div>

      <div class="stats">
        <span
          >项目总数 <strong>{{ total }}</strong></span
        >
      </div>
    </div>

    <!-- 使用 vant 下拉刷新组件 -->
    <van-pull-refresh
      v-model="isRefreshing"
      @refresh="onRefresh"
      class="pull-refresh-wrapper"
    >
      <!-- 骨架屏 -->
      <div v-if="loading && list.length === 0" class="skeleton-wrapper">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-line w-60"></div>
          <div class="skeleton-line w-40"></div>
          <div class="skeleton-line w-80"></div>
          <div class="skeleton-line w-50"></div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading && list.length === 0" class="empty-state">
        <svg viewBox="0 0 120 120" fill="none" width="120" height="120">
          <circle cx="60" cy="60" r="50" fill="#E8F1FB" />
          <rect
            x="34"
            y="36"
            width="52"
            height="48"
            rx="6"
            fill="white"
            stroke="#4A90D9"
            stroke-width="2"
          />
          <rect x="44" y="44" width="32" height="4" rx="2" fill="#D0E3F9" />
          <rect x="44" y="54" width="20" height="4" rx="2" fill="#D0E3F9" />
          <rect x="44" y="64" width="26" height="4" rx="2" fill="#D0E3F9" />
          <circle cx="74" cy="66" r="6" stroke="#B3D4F7" stroke-width="1.5" />
          <path
            d="M72 64L76 68M76 64L72 68"
            stroke="#2B7ECF"
            stroke-width="1.5"
          />
        </svg>
        <span>暂无项目</span>
      </div>

      <!-- 列表 -->
      <div v-else class="list">
        <div
          v-for="item in list"
          :key="item.id"
          class="settlement-card"
          @click="goToDetail(item)"
        >
          <div class="card-header">
            <span class="project-name">{{ item.name }}</span>
            <span class="amount">{{ item.salaryRange }}</span>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">发布企业</span>
              <span class="value">{{ item.merchantName }}</span>
            </div>
            <div class="info-row">
              <span class="label">发布时间</span>
              <span class="value">{{ item.createTime }}</span>
            </div>
            <div class="info-row">
              <span class="label">状态</span>
              <span
                class="value"
                :class="item.status == 1 ? 'status-active' : 'status-finished'"
              >
                {{
                  item.statusName || (item.status == 1 ? "进行中" : "已结束")
                }}
              </span>
            </div>
            <div class="info-row desc">
              <span class="label">项目描述</span>
              <span class="value desc-text">{{
                item.describe || "无描述"
              }}</span>
            </div>
            <!-- <div class="info-row actions">
              <button class="btn-detail">查看详情</button>
            </div> -->
          </div>
        </div>
        <!-- 底部哨兵元素（用于触发上拉加载） -->
        <div ref="sentinel" class="sentinel"></div>
      </div>

      <!-- 加载更多状态 -->
      <div class="load-more-status">
        <span v-if="loading && list.length > 0"
          ><van-icon name="loading" size="16" /> 加载更多...</span
        >
        <span v-else-if="finished && list.length > 0">— 已全部加载 —</span>
      </div>
    </van-pull-refresh>

    <footer class="page-footer">
      <span>共 {{ total }} 个项目</span>
    </footer>
  </div>
</template>

<script>
import { Toast } from "vant";
import { httpPost } from "@/utils/fetch";

export default {
  name: "ProjectPage",
  data() {
    return {
      list: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      finished: false,
      isRefreshing: false,
      observer: null,
    };
  },
  mounted() {
    this.fetchData(true);
    this.$nextTick(() => {
      this.initObserver();
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    initObserver() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel) return;
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.loadMore();
          }
        },
        { root: null, threshold: 0.1 }
      );
      this.observer.observe(sentinel);
    },

    // ---------- 数据获取 ----------
    fetchData(refresh = false) {
      if (this.loading) return;
      this.loading = true;

      const pageNum = refresh ? 1 : this.page;
      const pageSize = this.pageSize;

      httpPost("/wechat/wechat/mineProject", { pageNum, pageSize })
        .then((data) => {
          this.loading = false;
          this.isRefreshing = false;

          if (data.code === "0000") {
            const items = data.data.rows || [];
            if (refresh) {
              this.list = items;
              this.page = 1;
            } else {
              this.list = this.list.concat(items);
              this.page = pageNum + 1;
            }
            this.total = data.data.total || 0;
            this.finished = this.list.length >= this.total;
            if (refresh) {
              Toast.success("刷新成功");
            }
          } else {
            Toast.fail(data.message || "获取数据失败");
          }
          this.$nextTick(() => {
            this.initObserver();
          });
        })
        .catch((error) => {
          this.loading = false;
          this.isRefreshing = false;
          Toast.fail("获取项目列表失败，请稍后重试");
          console.error("获取项目列表失败:", error);
          this.$nextTick(() => {
            this.initObserver();
          });
        });
    },

    // ---------- 下拉刷新 ----------
    onRefresh() {
      this.finished = false;
      this.fetchData(true);
    },

    // ---------- 上拉加载更多（由 observer 触发） ----------
    loadMore() {
      if (this.loading || this.finished) return;
      this.fetchData(false);
    },

    // ---------- 跳转详情 ----------
    goToDetail(item) {
      // const id = item.id;
      // this.$router.push({ path: "/projectSquare/detail", query: { id } });
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}
.project-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f7fd;
  padding: 0 16px 12px;
  max-width: 820px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #1a2332;
  overflow: hidden;
}

.pull-refresh-wrapper {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.skeleton-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
}
.skeleton-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.skeleton-line {
  height: 14px;
  background: #eef1f5;
  border-radius: 8px;
  animation: pulse 1.2s ease-in-out infinite;
}
.skeleton-line.w-60 {
  width: 60%;
}
.skeleton-line.w-40 {
  width: 40%;
}
.skeleton-line.w-80 {
  width: 80%;
}
.skeleton-line.w-50 {
  width: 50%;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
  color: #8a99b0;
  font-size: 16px;
}
.empty-state .van-icon {
  opacity: 0.5;
  margin-bottom: 8px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 10px;
  padding-bottom: 100px;
}

.settlement-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 18px 20px 16px;
  box-shadow: 0 2px 12px rgba(0, 20, 50, 0.04);
  transition: box-shadow 0.25s, transform 0.15s;
}
.settlement-card:active {
  transform: scale(0.99);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.project-name {
  font-size: 17px;
  font-weight: 600;
  color: #0b1a33;
}

.amount {
  font-size: 14px;
  color: #e74c5e;
  font-weight: 600;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-row {
  display: flex;
  font-size: 14px;
  line-height: 1.6;
}

.label {
  width: 76px;
  flex-shrink: 0;
  color: #7a88a6;
}

.value {
  color: #1a2332;
}

.status-active {
  color: #07c160;
}
.status-finished {
  color: #999;
}

.desc {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e8ecf2;
}
.desc-text {
  color: #5a6a82;
  word-break: break-all;
}

.actions {
  margin-top: 8px;
  justify-content: flex-end;
}
.btn-detail {
  background: transparent;
  border: 1px solid #4f7cff;
  color: #4f7cff;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s;
  background: #f5f9ff;
}
.btn-detail:active {
  transform: scale(0.95);
  background: #e6efff;
}

.sentinel {
  height: 1px;
  margin-top: -1px;
}

.load-more-status {
  text-align: center;
  font-size: 13px;
  color: #a0abb9;
  padding: 12px 0 8px;
}

.page-footer {
  text-align: center;
  font-size: 12px;
  color: #b0bac9;
  padding: 10px 0 4px;
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.03);
}
.header-area {
  background: #ffffff;
  margin: 0 -16px 10px -16px;
  padding: 0 20px 12px 20px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 2px 12px rgba(0, 20, 50, 0.06);
  flex-shrink: 0;
}
.header {
  padding: 18px 0 4px 0;
}
.header .title {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #1989fa 0%, #07c160 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}
.stats {
  padding: 4px 0 6px 0;
}
.stats span {
  background: #f5f7fa;
  border-radius: 20px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #7a88a6;
  border: 1px solid #e8ecf2;
}
.stats strong {
  color: #e74c5e;
  font-size: 28px;
  margin: 0 4px;
  font-weight: 700;
}

@media (max-width: 480px) {
  .header-area {
    margin: 0 -10px 0 -10px;
    padding: 0 16px 12px 16px;
  }
  .header .title {
    font-size: 20px;
  }
  .stats span {
    font-size: 14px;
    padding: 6px 14px;
  }
  .stats strong {
    font-size: 24px;
  }
  .project-page {
    padding: 0 10px 10px;
  }
  .stats-card {
    padding: 14px 16px;
  }
  .stats-number {
    font-size: 24px;
  }
  .settlement-card {
    padding: 14px 16px 12px;
  }
  .project-name {
    font-size: 15px;
  }
  .info-row {
    font-size: 13px;
  }
  .label {
    width: 68px;
  }
}
</style>